import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import AuthImage from "../components/AuthImage";
import { ArrowLeft, Printer, Plus, ArrowUpRight, ArrowDownRight, Move, X, Loader2, Camera, Trash2, ImagePlus, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import SearchableSelect from "../components/SearchableSelect";
import ConfirmDialog from "../components/ConfirmDialog";
import Lightbox from "../components/Lightbox";

export default function LotDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { can, user } = useAuth();
  const [lot, setLot] = useState(null);
  const [addQty, setAddQty] = useState(sp.get("append") ? "" : "");
  const [addEmp, setAddEmp] = useState(user?.full_name || "");
  const [busy, setBusy] = useState(false);
  const [rerack, setRerack] = useState(null);
  const [racks, setRacks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingStone, setUploadingStone] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => setLot((await api.get(`/lots/${id}`)).data);
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/warehouses").then(r => setWarehouses(r.data));
  }, []);
  // Rerack modal: reload rack options scoped to the chosen destination
  // warehouse so operators can only pick racks that physically belong there.
  useEffect(() => {
    if (!rerack) return;
    const params = rerack.new_warehouse ? { warehouse: rerack.new_warehouse } : {};
    api.get("/racks", { params }).then(r => {
      setRacks(r.data);
      // Drop the current rack selection if it no longer belongs to the
      // chosen warehouse (prevents stale invalid picks).
      if (rerack.new_rack && !r.data.some(x => x.name === rerack.new_rack)) {
        setRerack(prev => prev ? { ...prev, new_rack: "" } : prev);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerack?.new_warehouse, rerack !== null]);

  const doAdd = async (e) => {
    e.preventDefault();
    if (!addQty || !addEmp) return;
    setBusy(true);
    try {
      await api.post(`/lots/${id}/add-stock`, {
        quantity: parseInt(addQty, 10), employee_name: addEmp,
      });
      toast.success(`Added ${addQty} pcs. Opening label print…`);
      const q = addQty; setAddQty("");
      nav(`/labels?lot=${id}&count=${q}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); load(); }
  };

  const submitRerack = async () => {
    const q = parseInt(rerack.quantity, 10);
    if (!q || q <= 0 || q > lot.quantity) { toast.error("Enter valid quantity"); return; }
    if (!rerack.new_rack) { toast.error("Pick new rack"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/lots/${id}/rerack`, {
        quantity: q, from_warehouse: lot.warehouse_place,
        new_warehouse: rerack.new_warehouse, new_rack: rerack.new_rack,
        employee_name: user?.full_name || user?.email,
      });
      toast.success(`Moved ${q} pcs to Rack ${rerack.new_rack}${r.data.split ? ' (new lot created)' : ''}`);
      setRerack(null);
      // Print labels for the moved qty (unless category has barcode disabled)
      const targetId = r.data.new_lot_id || id;
      nav(`/labels?lot=${targetId}&count=${q}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // -------- Photo management: add / replace / remove --------
  const savePhotos = async (nextPhotos) => {
    try {
      const r = await api.put(`/lots/${id}/photos`, { photos: nextPhotos });
      setLot({ ...lot, photos: r.data.photos });
      toast.success("Photos updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo update failed");
    }
  };
  const uploadNewPhoto = async (file, replaceIdx) => {
    if (!file) return;
    setUploading(true);
    try {
      const nextIndex = replaceIdx != null ? replaceIdx + 1 : (lot.photos || []).length + 1;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("batch_no", lot?.lot_no || "NOBATCH");
      fd.append("stage", "lot");
      fd.append("index", String(nextIndex));
      const up = await api.post("/uploads", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...(lot.photos || [])];
      const entry = {
        storage_path: up.data.storage_path,
        sequence_index: up.data.sequence_index,
        label: replaceIdx != null ? (next[replaceIdx]?.label || `Photo ${replaceIdx + 1}`) : `Photo ${next.length + 1}`
      };
      if (replaceIdx != null) next[replaceIdx] = entry; else next.push(entry);
      await savePhotos(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const removePhoto = async (i) => {
    if (!window.confirm("Remove this photo?")) return;
    const next = (lot.photos || []).filter((_, idx) => idx !== i);
    await savePhotos(next);
  };

  // -------- Stone block reference photo (single, optional, audited) --------
  const uploadStoneBlock = async (file) => {
    if (!file) return;
    // Instant local preview while we upload — cleared on completion/error.
    const preview = URL.createObjectURL(file);
    setLot(l => ({ ...l, _stone_preview: preview }));
    setUploadingStone(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("batch_no", lot?.lot_no || "NOBATCH");
      fd.append("stage", "stone_block");
      fd.append("index", "1");
      const up = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      const r = await api.put(`/lots/${id}/stone-block-photo`,
        { storage_path: up.data.storage_path });
      setLot(l => ({
        ...l,
        stone_block_image: r.data.stone_block_image,
        _stone_preview: null,
      }));
      toast.success("Stone block photo updated");
    } catch (err) {
      // Offline / dropped connection → save the file locally so it retries
      // automatically as soon as the browser comes back online.
      const isNet = !err.response && (err.code === "ERR_NETWORK" || !navigator.onLine);
      if (isNet) {
        try {
          const { enqueue } = await import("../lib/uploadQueue");
          // No token embedded — the retry uses the HTTP-only auth cookie
          // that the browser sends automatically on same-origin requests.
          await enqueue({ file, endpoint: "/api/uploads", fieldName: "file",
            kind: "stone_block", ref: id });
          toast.warning("Offline — photo queued, will upload when you're back online");
        } catch (e2) { toast.error("Upload failed and could not be queued"); }
      } else {
        toast.error(err.response?.data?.detail || "Upload failed");
      }
      setLot(l => ({ ...l, _stone_preview: null }));
    } finally {
      URL.revokeObjectURL(preview);
      setUploadingStone(false);
      setUploadPct(0);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/lots/${id}`);
      toast.success(`Lot ${lot.lot_no} permanently removed`);
      setDeleteOpen(false);
      nav("/stock");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  if (!lot) return <div className="text-[#A8998A] text-sm">Loading…</div>;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="lot-detail">
      <Link to="/stock" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622]">
        <ArrowLeft size={14}/> Back to stock
      </Link>
      <header className="flex flex-wrap gap-4 md:gap-6 justify-between items-start">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">{lot.category_name}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#2D2622]">{lot.color_name}</h1>
          <div className="mt-2 font-mono2 text-sm text-[#5D5148] flex flex-wrap gap-2">
            <span>{lot.size_label}</span><span>·</span>
            <span>Lot {lot.lot_no}</span><span>·</span>
            <span>Rack <b className="text-[#2D2622]">{lot.rack_no}</b></span>
            {lot.warehouse_place && <><span>·</span><span>{lot.warehouse_place}</span></>}
            {lot.factory_no && <><span>·</span><span>{lot.factory_no}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#8B6F47]">Available</div>
            <div className="font-mono2 text-4xl md:text-5xl font-bold leading-none text-[#2D2622]">{lot.quantity}</div>
          </div>
          {can("stock.edit") && (
            <button onClick={() => setRerack({ quantity: lot.quantity, new_warehouse: lot.warehouse_place || "", new_rack: "" })} data-testid="rerack-btn"
              className="h-11 px-4 border border-[#E5D8C4] bg-white text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:border-[#2D2622]">
              <Move size={14}/> Rerack
            </button>
          )}
          {can("labels.print") && (
            <Link to={`/labels?lot=${lot.id}`} data-testid="print-labels-btn"
              className="inline-flex items-center gap-2 h-11 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E]">
              <Printer size={14}/> Print labels
            </Link>
          )}
          {isAdmin && (
            <button onClick={() => setDeleteOpen(true)} data-testid="delete-lot-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-red-300 bg-white text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-50">
              <Trash2 size={14}/> Delete lot
            </button>
          )}
          {can("stock.edit") && (
            <button onClick={() => setAdjustOpen(true)} data-testid="adjust-qty-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-[#E5D8C4] bg-white text-[#2D2622] text-xs uppercase tracking-[0.2em] hover:border-[#2D2622]">
              <Edit3 size={14}/> Adjust quantity
            </button>
          )}
        </div>
      </header>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Photos</div>
          {can("stock.edit") && (
            <label data-testid="add-photo-btn"
              className="h-9 px-3 border border-[#E5D8C4] bg-white text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:border-[#2D2622] cursor-pointer">
              {uploading ? <Loader2 className="animate-spin" size={13}/> : <ImagePlus size={13}/>}
              Add photo
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadNewPhoto(e.target.files?.[0])} />
            </label>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {lot.photos.map((p, i) => (
            <div key={p.storage_path || `photo-${i}`} className="border border-[#E5D8C4] bg-white group relative" data-testid={`lot-photo-${i}`}>
          <div className="aspect-[4/3] bg-[#F3ECDF] overflow-hidden cursor-zoom-in"
            onClick={() => setLightboxIdx(i)} data-testid={`lot-photo-open-${i}`}>
            <AuthImage path={p.storage_path} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
          </div>
              {can("stock.edit") && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <label className="w-8 h-8 bg-white/95 border border-[#E5D8C4] flex items-center justify-center hover:border-[#2D2622] cursor-pointer"
                    title="Replace" data-testid={`lot-photo-replace-${i}`}>
                    <Camera size={13}/>
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadNewPhoto(e.target.files?.[0], i)} />
                  </label>
                  <button onClick={() => removePhoto(i)}
                    data-testid={`lot-photo-remove-${i}`}
                    className="w-8 h-8 bg-white/95 border border-[#E5D8C4] flex items-center justify-center hover:border-red-500 hover:text-red-600" title="Remove">
                    <Trash2 size={13}/>
                  </button>
                </div>
              )}
              {p.label && <div className="p-3 text-xs text-[#5D5148] font-medium">{p.label}</div>}
            </div>
          ))}
          {lot.photos.length === 0 && (
            <div className="col-span-full text-sm text-[#8B6F47] italic py-4">No photos yet.</div>
          )}
        </div>
      </section>

      {can("stock.add") && (
        <section className="bg-white border border-[#E5D8C4] p-4 md:p-6">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-4 flex items-center gap-2">
            <Plus size={14}/> Add more sheets to this lot
          </div>
          <form onSubmit={doAdd} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input type="number" min="1" required data-testid="addmore-qty" placeholder="Quantity"
              value={addQty} onChange={(e) => setAddQty(e.target.value)}
              className="h-11 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622] font-mono2" />
            <input required data-testid="addmore-emp" placeholder="Employee name"
              value={addEmp} onChange={(e) => setAddEmp(e.target.value)}
              className="h-11 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
            <button disabled={busy} data-testid="addmore-submit"
              className="h-11 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] disabled:opacity-60">
              Add & print labels
            </button>
          </form>
        </section>
      )}

      {/* Stone Block Photo — optional reference image, per-lot with audit metadata */}
      <section className="bg-white border border-[#E5D8C4] p-4 md:p-6" data-testid="stone-block-photo">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Stone Block Photo</div>
            <div className="text-xs text-[#8B6F47] mt-1">Optional reference image of the source stone block.</div>
          </div>
          <div className="text-right text-[11px] text-[#8B6F47]">
            {lot.stone_block_image?.updated_at ? (
              <>
                <div>Last updated</div>
                <div className="font-mono2 text-[#2D2622]">
                  {new Date(lot.stone_block_image.updated_at).toLocaleString("en-GB", {
                    day:"2-digit", month:"2-digit", year:"numeric",
                    hour:"2-digit", minute:"2-digit", hour12:true })}
                </div>
                <div className="mt-1">By <b className="text-[#2D2622]">{lot.stone_block_image.updated_by_name || "—"}</b></div>
              </>
            ) : <div className="italic">Not uploaded yet</div>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
          <div className="aspect-square bg-[#F3ECDF] border border-[#E5D8C4] overflow-hidden flex items-center justify-center">
            {lot._stone_preview ? (
              <img src={lot._stone_preview} alt="uploading"
                className="w-full h-full object-cover opacity-70" />
            ) : lot.stone_block_image?.storage_path ? (
              <AuthImage path={lot.stone_block_image.storage_path}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightboxIdx("stone")} />
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#A8998A]">No photo</span>
            )}
          </div>
          {can("stock.edit") && (
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <label data-testid="stone-block-gallery"
                  className={`inline-flex items-center justify-center gap-2 h-10 px-4 border border-[#E5D8C4] bg-white text-xs uppercase tracking-widest hover:border-[#2D2622] cursor-pointer flex-1 ${uploadingStone ? "opacity-50 pointer-events-none" : ""}`}>
                  {uploadingStone ? <Loader2 className="animate-spin" size={13}/> : <ImagePlus size={13}/>}
                  Upload from Gallery
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadStoneBlock(e.target.files?.[0])} />
                </label>
                <label data-testid="stone-block-camera"
                  className={`inline-flex items-center justify-center gap-2 h-10 px-4 border border-[#E5D8C4] bg-white text-xs uppercase tracking-widest hover:border-[#2D2622] cursor-pointer flex-1 ${uploadingStone ? "opacity-50 pointer-events-none" : ""}`}>
                  {uploadingStone ? <Loader2 className="animate-spin" size={13}/> : <Camera size={13}/>}
                  Take Photo (Camera)
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => uploadStoneBlock(e.target.files?.[0])} />
                </label>
              </div>
              <div className="text-[10px] text-[#8B6F47]">
                Uploads are audit-logged; metadata refreshes instantly after each replace. Max ~15 MB per image.
              </div>
              {uploadingStone && (
                <div className="mt-2" data-testid="stone-block-progress">
                  <div className="flex justify-between text-[10px] text-[#8B6F47] mb-1">
                    <span>Uploading photo…</span>
                    <span className="font-mono2">{uploadPct}%</span>
                  </div>
                  <div className="h-2 bg-[#F3ECDF] overflow-hidden">
                    <div className="h-full bg-[#2D2622] transition-all"
                         style={{ width: `${uploadPct}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Movement history</div>
        <div className="bg-white border border-[#E5D8C4] divide-y divide-[#F3ECDF]">
          {lot.movements?.length === 0 && <div className="p-4 text-sm text-[#A8998A]">No movements yet.</div>}
          {lot.movements?.map(m => (
            <div key={m.id} className="p-3 md:p-4 flex items-center gap-3 md:gap-4">
              <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${
                m.type==='IN'?'bg-green-50 text-green-700':
                m.type==='OUT'?'bg-orange-50 text-orange-700':
                'bg-blue-50 text-blue-700'
              }`}>
                {m.type === 'IN' ? <ArrowDownRight size={13}/> :
                 m.type === 'OUT' ? <ArrowUpRight size={13}/> : <Move size={13}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2D2622]">
                  {m.type} {m.quantity > 0 && <>· <span className="font-mono2">{m.quantity}</span> pcs</>} · {m.reference}
                </div>
                <div className="text-[10px] text-[#8B6F47] font-mono2 truncate">
                  {m.employee_name} · {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {rerack && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRerack(null)}>
          <div className="bg-white w-full max-w-md border border-[#E5D8C4]" onClick={(e)=>e.stopPropagation()}>
            <div className="p-5 border-b border-[#F3ECDF] flex justify-between items-start">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Move rack</div>
                <div className="font-display font-bold text-xl text-[#2D2622] mt-1">Rerack lot</div>
                <div className="text-xs text-[#5D5148] mt-1 font-mono2">
                  Currently: <b className="text-[#2D2622]">{lot.rack_no}</b>
                  {lot.warehouse_place && <> · <b>{lot.warehouse_place}</b></>} · {lot.quantity} pcs
                </div>
              </div>
              <button onClick={() => setRerack(null)} className="p-1"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">1. Quantity to move</div>
                <input type="number" min="1" max={lot.quantity} value={rerack.quantity}
                  onChange={(e) => setRerack({ ...rerack, quantity: e.target.value })}
                  data-testid="rerack-qty"
                  className="w-full h-12 px-3 border border-[#E5D8C4] font-mono2 text-2xl font-bold text-center focus:outline-none focus:border-[#2D2622]" />
                <div className="text-[11px] text-[#8B6F47] mt-1">
                  Move partial qty → creates a new lot at destination.
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">2. Destination warehouse</div>
                <SearchableSelect testId="rerack-warehouse" value={rerack.new_warehouse}
                  onChange={(v) => setRerack({ ...rerack, new_warehouse: v })}
                  options={warehouses.map(w => ({ value: w.name, label: w.name, sub: w.note }))}
                  placeholder="Select warehouse…" />
              </div>
              <div>
                <div className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2">3. New rack</div>
                <SearchableSelect testId="rerack-select" value={rerack.new_rack}
                  onChange={(v) => setRerack({ ...rerack, new_rack: v })}
                  options={racks.map(r => ({ value: r.name, label: r.name }))}
                  placeholder="Search or pick new rack…" />
                {rerack.new_warehouse && racks.length === 0 && (
                  <div data-testid="rerack-no-racks" className="text-[11px] text-[#C2410C] mt-1">
                    No racks are assigned to <b>{rerack.new_warehouse}</b> yet. Add one in Settings › Racks.
                  </div>
                )}
              </div>
              <button onClick={submitRerack} disabled={busy || !rerack.new_rack || !rerack.quantity}
                data-testid="rerack-submit"
                className="w-full h-11 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {busy ? <Loader2 className="animate-spin" size={14}/> : <>Move & print new labels</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={doDelete}
        loading={deleting}
        tone="danger"
        title="Permanently delete this lot?"
        testId="delete-lot-dialog"
        message={<>
          You're about to permanently remove <b>Lot {lot.lot_no}</b> ({lot.category_name} · {lot.color_name} · {lot.size_label})
          with <b>{lot.quantity} pcs</b> currently on Rack <b>{lot.rack_no}</b>. This cannot be undone — its history is retained for audit,
          but the lot will disappear from every list, filter, and PO allocation. Continue?
        </>}
        confirmLabel="Yes, delete lot"
        cancelLabel="Cancel" />

      {lightboxIdx !== null && lightboxIdx !== "stone" && (
        <Lightbox photos={lot.photos || []} startIndex={lightboxIdx}
          title={`Lot ${lot.lot_no}`}
          onClose={() => setLightboxIdx(null)} />
      )}
      {lightboxIdx === "stone" && lot.stone_block_image && (
        <Lightbox photos={[lot.stone_block_image]} startIndex={0}
          title={`Lot ${lot.lot_no} — Stone Block`}
          onClose={() => setLightboxIdx(null)} />
      )}

      {adjustOpen && (
        <AdjustQuantityDialog
          lot={lot} isAdmin={isAdmin}
          onClose={() => setAdjustOpen(false)}
          onSaved={(newQty) => { setLot(l => ({ ...l, quantity: newQty })); setAdjustOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/** Manual quantity override. Admin: direct. Non-admin: request OTP mailed to
 *  every admin, then submit code with the new quantity. */
function AdjustQuantityDialog({ lot, isAdmin, onClose, onSaved }) {
  const [qty, setQty] = useState(String(lot.quantity));
  const [reason, setReason] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(isAdmin ? "form" : "request");
  const [busy, setBusy] = useState(false);

  const requestOtp = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      const r = await api.post(`/lots/${lot.id}/request-adjust-otp`);
      toast.success(r.data.message || "Approval code sent to admin email");
      setStep("verify");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send OTP"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    const q = parseInt(qty, 10);
    if (Number.isNaN(q) || q < 0) return toast.error("Enter a valid quantity");
    if (!reason.trim()) return toast.error("Reason is required (e.g., breakage, recount)");
    if (!isAdmin && !otp.trim()) return toast.error("Enter admin OTP");
    setBusy(true);
    try {
      const r = await api.post(`/lots/${lot.id}/set-quantity`,
        { quantity: q, reason: reason.trim(), otp: isAdmin ? undefined : otp.trim() });
      toast.success(`Quantity updated to ${r.data.quantity}`);
      onSaved(r.data.quantity);
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="adjust-qty-dialog">
      <div className="bg-white border border-[#E5D8C4] w-full max-w-md p-5 space-y-3" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Adjust quantity</div>
            <div className="text-xs text-[#8B6F47] mt-1">
              Lot <b className="text-[#2D2622]">{lot.lot_no}</b> · currently <b>{lot.quantity} pcs</b>
              {!isAdmin && " · needs admin approval"}
            </div>
          </div>
          <button onClick={onClose} className="text-[#8B6F47] hover:text-[#2D2622]"><X size={16}/></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1 block">New quantity</label>
          <input type="number" min="0" value={qty} onChange={(e)=>setQty(e.target.value)}
            data-testid="adjust-qty-input"
            disabled={step === "verify"}
            className="w-full h-10 px-3 border border-[#E5D8C4] font-mono2 text-lg focus:outline-none focus:border-[#2D2622]" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1 block">Reason (required)</label>
          <input value={reason} onChange={(e)=>setReason(e.target.value)}
            data-testid="adjust-reason"
            disabled={step === "verify"}
            placeholder="e.g. Recount, breakage, damaged in transit"
            className="w-full h-10 px-3 border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
        </div>

        {!isAdmin && step === "verify" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold mb-1 block">Admin approval code</label>
            <input value={otp} onChange={(e)=>setOtp(e.target.value)}
              data-testid="adjust-otp" inputMode="numeric" maxLength={6}
              placeholder="6-digit code from admin's email"
              className="w-full h-10 px-3 border border-[#E5D8C4] text-sm font-mono2 tracking-[0.3em] text-center focus:outline-none focus:border-[#2D2622]" />
            <button onClick={requestOtp} disabled={busy} type="button"
              className="text-[11px] text-[#8B6F47] hover:text-[#2D2622] underline mt-1">
              {busy ? "Sending…" : "Resend code"}
            </button>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5D8C4] text-xs uppercase tracking-widest">Cancel</button>
          {!isAdmin && step === "request" ? (
            <button onClick={requestOtp} disabled={busy} data-testid="adjust-request-otp"
              className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40 inline-flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={13}/> : null}
              Request admin OTP
            </button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="adjust-submit"
              className="h-10 px-5 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40 inline-flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={13}/> : null}
              Update quantity
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
